/**
 * GeneratingPaths Context
 *
 * Lightweight context that carries the set of override PATHS whose generator
 * fields are actively being generated by the editor. The Editable runtime
 * components (`EditableToken`, `EditableSlotGroup`) read this set and, when
 * their own path is in it, render a shimmer skeleton over just that field —
 * leaving the rest of the section's static content visible.
 *
 * CRITICAL — deployed-site safety:
 * The default value is an EMPTY set, and the ONLY place that ever provides a
 * non-empty value is the editor's `PreviewFrame` (via `GeneratingPathsProvider`).
 * Deployed / published sites never render `PreviewFrame`, so they never wrap the
 * tree in this provider; `useGeneratingPaths()` therefore returns the empty
 * default and no skeleton can ever appear on a live site. Do NOT introduce any
 * always-on population of this context.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useEditMode } from './edit-mode';

// Stable empty default so the context never reports a "generating" path outside
// the editor preview. The ReadonlySet typing blocks mutation at compile time.
const EMPTY_PATHS: ReadonlySet<string> = new Set<string>();

const GeneratingPathsContext = createContext<ReadonlySet<string>>(EMPTY_PATHS);

/**
 * Provider for the actively-generating override paths. Only the editor
 * `PreviewFrame` should render this; everywhere else the empty default applies.
 */
export function GeneratingPathsProvider({
  paths,
  children,
}: {
  paths: ReadonlySet<string>;
  children: ReactNode;
}) {
  return (
    <GeneratingPathsContext.Provider value={paths}>
      {/* Inject the skeleton CSS once per provider (editor preview only) rather
          than once per generating field, since every GeneratingSkeleton renders
          under this provider. */}
      <style>{generatingSkeletonStyles}</style>
      {children}
    </GeneratingPathsContext.Provider>
  );
}

/**
 * Returns the set of override paths currently generating. Empty everywhere
 * except inside the editor preview, so callers can safely gate skeleton UI on
 * `useGeneratingPaths().has(path)` without risking it on deployed sites.
 */
export function useGeneratingPaths(): ReadonlySet<string> {
  return useContext(GeneratingPathsContext);
}

/**
 * Wraps `children` in a generating skeleton when `path` is actively generating,
 * else returns `children` unchanged. Single source of truth for the deployed-site
 * safety gate: it requires BOTH the EditMode provider and a populated
 * GeneratingPaths provider, neither of which deployed/SSG renders mount — so off
 * the editor preview this is always a zero-allocation pass-through.
 */
export function useGeneratingContent(
  path: string,
  children: ReactNode,
  as: 'span' | 'div',
): ReactNode {
  const editMode = useEditMode();
  const generatingPaths = useGeneratingPaths();
  const isGenerating = !!editMode && generatingPaths.has(path);
  return isGenerating ? <GeneratingSkeleton as={as}>{children}</GeneratingSkeleton> : children;
}

/**
 * GeneratingSkeleton
 *
 * Wraps a single editable field (token or slot group) in a shimmer overlay
 * while it is generating. The real content stays mounted but visually hidden
 * (`visibility: hidden`) so the skeleton fills the field's EXACT footprint —
 * no reflow when it toggles on/off. The shimmer CSS is injected once by
 * `GeneratingPathsProvider` (the only place this ever renders).
 *
 * Editor-preview only: it is only ever rendered when `useGeneratingPaths()`
 * reports a match, which can't happen on deployed sites (empty default).
 */
export function GeneratingSkeleton({
  children,
  as = 'span',
}: {
  children: ReactNode;
  /** 'span' for inline tokens, 'div' for block-level slot groups. */
  as?: 'span' | 'div';
}) {
  const Tag = as;
  return (
    <Tag
      className="vibesites-field-generating"
      style={{ position: 'relative', display: as === 'span' ? 'inline-block' : 'block' }}
      role="status"
      aria-busy="true"
      aria-label="Generating content"
    >
      {/* Preserve footprint without showing the (about-to-change) content. */}
      <span aria-hidden style={{ visibility: 'hidden', pointerEvents: 'none' }}>
        {children}
      </span>
      <span className="vibesites-field-generating-overlay" aria-hidden>
        <span className="vibesites-field-generating-shimmer" />
      </span>
    </Tag>
  );
}

const generatingSkeletonStyles = `
.vibesites-field-generating-overlay {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  z-index: 2;
}
.vibesites-field-generating-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    #eef0f3 0%,
    #e2e5ea 20%,
    #f3f4f6 40%,
    #eef0f3 60%,
    #eef0f3 100%
  );
  background-size: 200% 100%;
  animation: vibesitesFieldGenShimmer 1.4s ease-in-out infinite;
}
@keyframes vibesitesFieldGenShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
`;
